import {
  IPaymentMethodResp,
  PaymentMethodStatusEnum,
  PaymentMethodTypeEnum,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  ACH_OR_BANK_TRANSFER,
  PaymentMethodStatusEnumDisplay,
  PaymentMethodTypeEnumDisplay,
} from '../../../constants/paymentMethods';
import { getAccountPaymentMethodView } from '../../../constants/routes';
import { formatAccountNumber, formatExpirationDate } from '../../../utils';

const columnHelper = createColumnHelper<IPaymentMethodResp>();

export interface PaymentMethodListColumnContext extends ColumnContextBase<IPaymentMethodResp> {
  accountId: string;
}

/** tableId: external (component-local filters) */
export const PAYMENT_METHOD_LIST_CATALOG: ColumnCatalog<
  IPaymentMethodResp,
  PaymentMethodListColumnContext
> = [
  {
    id: 'paymentMethodName',
    label: 'Name',
    customizable: false,
    build: ({ accountId }) =>
      columnHelper.accessor('paymentMethodName', {
        ...getColHeaderWithSize('Name', 250),
        ...getColHeaderFilters('TEXT'),
        cell: nameWithIdBodyTemplate<IPaymentMethodResp, unknown>({
          idProp: 'id',
          nameProp: 'paymentMethodName',
          idLinkFn: (id) => getAccountPaymentMethodView(accountId, id),
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'paymentMethodType',
    label: 'Type',
    build: () =>
      columnHelper.accessor('paymentMethodType', {
        ...getColHeaderWithSize('Type', 200),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            PaymentMethodTypeEnum,
            PaymentMethodTypeEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: PaymentMethodTypeEnumDisplay,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'number',
    label: 'Number',
    build: () =>
      columnHelper.display({
        id: 'number',
        ...getColHeaderWithSize('Number', 180),
        cell: (info) => {
          const row = info.row.original;
          return (
            <MText truncate lineClamp={1}>
              {ACH_OR_BANK_TRANSFER.has(row.paymentMethodType)
                ? row.accountNumber
                : formatAccountNumber(row.lastFour)}
            </MText>
          );
        },
      }),
  },
  {
    id: 'expirationDate',
    label: 'Expiration Date',
    build: () =>
      columnHelper.display({
        id: 'expirationDate',
        ...getColHeaderWithSize('Expiration Date', 190),
        cell: (info) => {
          const row = info.row.original;
          return (
            <MText truncate lineClamp={1}>
              {formatExpirationDate(row.expirationMonth, row.expirationYear)}
            </MText>
          );
        },
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            PaymentMethodStatusEnum,
            PaymentMethodStatusEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: PaymentMethodStatusEnumDisplay,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        // ...getColHeaderFilters('DATE'), // BE needs to support
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        // ...getColHeaderFilters('DATE'), // BE needs to support
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IPaymentMethodResp>(),
];
